<template>
    <div style="width: 100%; height: 500px">
        <div ref="chart" style="width: 100%; height: 100%"></div>
    </div>
</template>

<script>
import * as echarts from "echarts";
import moment from "moment";
export default {
    props: {
        chartData: { type: Array, default: () => [] },
        pointInfo: { type: Object, default: () => {} },
        abnormalData: { type: Array, default: () => [] },
        abnormalDefs: { type: Array, default: () => [] },
        missingData: { type: Array, default: () => [] },
        useType: { type: String, default: "" },
        eventDetail: { type: Object, default: () => {} },
    },
    data() {
        return {
            chart: null,
            xAxisLabel: [],
            yAxisMax: null,
            beforeCorrection: [],
            afterCorrection: [],
            abnormalMarker: [], // 이상치 마커
            abnormalDefine: null, // 이상치 구간
            missingSection: null,
        };
    },
    components: {},
    computed: {},
    watch: {},
    created() {},
    async mounted() {
        await this.prepareData();
        this.initChart();
        window.addEventListener("resize", this.resizeChart);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.resizeChart);
        if (this.chart) this.chart.dispose();
    },
    methods: {
        resizeChart() {
            if (this.chart) this.chart.resize();
        },
        prepareData() {
            if (!this.isEmpty(this.chartData)) {
                let prevTime = null;
                this.afterCorrection = [];
                this.beforeCorrection = [];

                this.chartData.forEach((item) => {
                    const currTime = new Date(moment(item.regDt.toString(), "YYYYMMDDHHmmss"));
                    const ptVal = item.ptVal;
                    const ptValRaw = item.ptValRaw;

                    if (prevTime !== null) {
                        const diff = currTime.getTime() - prevTime.getTime();
                        if (diff > 6 * 60 * 1000) {
                            // 6분 초과면 선 끊기
                            this.afterCorrection.push(null);
                            this.beforeCorrection.push(null);
                        }
                    }

                    this.afterCorrection.push([currTime, ptVal]);
                    this.beforeCorrection.push([currTime, ptValRaw]);

                    prevTime = currTime;
                });

                const afterVals = this.afterCorrection.filter((v) => v !== null).map((v) => v[1]);
                const beforeVals = this.beforeCorrection.filter((v) => v !== null).map((v) => v[1]);

                const maxAfter = afterVals.length > 0 ? Math.max(...afterVals) : 0;
                const maxBefore = beforeVals.length > 0 ? Math.max(...beforeVals) : 0;
                const maxVal = Math.max(maxAfter, maxBefore);

                // 데이터가 모두 0인 경우 기본값 설정
                if (maxVal === 0) {
                    this.yAxisMax = 1;
                } else {
                    // 소수점 데이터 처리 개선
                    let margin;
                    if (maxVal < 0.1) {
                        // 매우 작은 값들 (0.1 미만)
                        margin = Math.max(maxVal * 0.5, 0.05);
                    } else if (maxVal < 1) {
                        // 0.1 ~ 1 사이의 값들
                        margin = Math.max(maxVal * 0.3, 0.1);
                    } else {
                        // 1 이상의 값들
                        margin = maxVal * 0.25;
                    }

                    let adjustedMax = maxVal + margin;

                    // Y축 최대값 계산 로직 개선
                    if (adjustedMax < 0.1) {
                        // 0.1 미만: 0.05 단위로 반올림
                        this.yAxisMax = Math.ceil(adjustedMax * 20) / 20;
                    } else if (adjustedMax < 1) {
                        // 0.1 ~ 1 사이: 0.1 단위로 반올림
                        this.yAxisMax = Math.ceil(adjustedMax * 10) / 10;
                    } else if (adjustedMax < 10) {
                        // 1 ~ 10 사이: 0.5 단위로 반올림
                        this.yAxisMax = Math.ceil(adjustedMax * 2) / 2;
                    } else {
                        // 10 이상: 정수로 반올림
                        this.yAxisMax = Math.ceil(adjustedMax);
                    }

                    // 최소값 보장
                    if (this.yAxisMax < 0.1) {
                        this.yAxisMax = 0.1;
                    }
                }
            }

            if (!this.isEmpty(this.abnormalData)) {
                this.abnormalMarker = this.abnormalData.map((item) => {
                    const time = new Date(moment(item.regDt.toString(), "YYYYMMDDHHmmss"));
                    return {
                        name: "Abnormal",
                        coord: [time, item.measuredVal],
                        value: item.measuredVal,
                        itemStyle: {
                            color: "#FB8C00",
                        },
                        abnormalData: { ...item },
                    };
                });
            }

            if (!this.isEmpty(this.abnormalDefs)) {
                const grayShades = ["#EEEEEE", "#E0E0E0", "#BDBDBD"];
                this.abnormalDefine = this.abnormalDefs.map((def, idx) => {
                    const color = grayShades[idx % grayShades.length];

                    return [
                        {
                            name: def.detectMethod,
                            xAxis: new Date(moment(def.startDt.toString(), "YYYYMMDDHHmmss")),
                            yAxis: def.lowerLimit,
                            itemStyle: {
                                color: color,
                                borderColor: "#BDBDBD", // 테두리 색상
                                borderWidth: 1, // 테두리 두께
                                borderType: "solid",
                            },
                            label: { show: false },
                        },
                        {
                            xAxis: new Date(moment(def.endDt.toString(), "YYYYMMDDHHmmss")),
                            yAxis: def.upperLimit,
                        },
                    ];
                });
            }

            if (!this.isEmpty(this.missingData)) {
                this.missingSection = this.missingData.map((d) => [
                    {
                        name: "결측",
                        xAxis: new Date(moment(d.startDt.toString(), "YYYYMMDDHHmmss")),
                        yAxis: 0,
                        itemStyle: {
                            color: "rgba(255, 0, 0, 0.1)",
                            borderColor: "#FFCDD2",
                            borderWidth: 1,
                            borderType: "solid",
                        },
                        label: {
                            show: true,
                            position: "insideTop",
                            color: "#000",
                            fontSize: 12,
                            formatter: "결측",
                        },
                    },
                    {
                        xAxis: d.endDt
                            ? new Date(moment(d.endDt.toString(), "YYYYMMDDHHmmss"))
                            : new Date(),
                        yAxis: this.yAxisMax,
                    },
                ]);
            }
        },
        initChart() {
            this.chart = echarts.init(this.$refs.chart);
            const verticalLines = [];
            const start = this.afterCorrection[0]?.[0];
            const end = this.afterCorrection[this.afterCorrection.length - 1]?.[0];
            if (start && end) {
                const startTime = moment(start).startOf("hour");
                const endTime = moment(end).endOf("hour");

                let current = startTime.clone();
                while (current.isBefore(endTime)) {
                    verticalLines.push({ xAxis: current.toDate() });
                    current.add(1, "hour");
                }
            }

            let dataZoomConfig = [];

            if (!this.isEmpty(this.eventDetail)) {
                if (this.eventDetail.alarmType == "AbnormalData") {
                    const eventTime = new Date(moment(this.eventDetail.regDt.toString(), "YYYYMMDDHHmmss"));
                    const zoomStartValue = moment(eventTime).subtract(1, "hours").toDate().valueOf();
                    const zoomEndValue = moment(eventTime).add(3, "hours").toDate().valueOf();

                    dataZoomConfig = [
                        {
                            type: "slider",
                            startValue: zoomStartValue,
                            endValue: zoomEndValue,
                            // minSpan: 30 * 60 * 1000,
                        },
                        {
                            type: "inside",
                            startValue: zoomStartValue,
                            endValue: zoomEndValue,
                            // minSpan: 30 * 60 * 1000,
                        },
                    ];
                } else if (this.eventDetail.alarmType == "MissingData") {
                    const eventTime = new Date(moment(this.eventDetail.startDt.toString(), "YYYYMMDDHHmmss"));
                    const zoomStartValue = moment(eventTime).subtract(1, "hours").toDate().valueOf();
                    const zoomEndValue = moment(eventTime).add(3, "hours").toDate().valueOf();

                    dataZoomConfig = [
                        {
                            type: "slider",
                            startValue: zoomStartValue,
                            endValue: zoomEndValue,
                            minSpan: 30 * 60 * 1000,
                        },
                        {
                            type: "inside",
                            startValue: zoomStartValue,
                            endValue: zoomEndValue,
                            minSpan: 30 * 60 * 1000,
                        },
                    ];
                }
            } else {
                dataZoomConfig = [
                    {
                        type: "slider",
                        start: 70,
                        end: 100,
                        minSpan: 10,
                    },
                    {
                        type: "inside",
                        start: 70,
                        end: 100,
                        minSpan: 10,
                    },
                ];
            }

            const option = {
                tooltip: {
                    trigger: "axis",
                    axisPointer: {
                        type: "cross",
                        label: {
                            backgroundColor: "#6a7985",
                        },
                    },
                },
                toolbox: {
                    show: true,
                },
                dataZoom: dataZoomConfig,
                legend: {
                    show: true,
                    right: 0,
                    selected: {
                        보정후: true,
                        보정전: true,
                        이상치구간: true, // 추가
                        결측: true, //
                    },
                },
                grid: {
                    top: 30,
                    left: 40,
                    right: 30,
                    bottom: 70,
                },
                xAxis: {
                    type: "time",
                    axisLabel: {
                        formatter: (val) => moment(val).format("HH:mm:ss"),
                    },
                },
                yAxis: {
                    type: "value",
                    min: 0,
                    max: this.yAxisMax || 1,
                    axisLabel: {
                        show: true,
                        formatter: (value) => {
                            if (value === 0) return "0";
                            return Number.isInteger(value) ? value : value.toFixed(1);
                        },
                    },
                    axisLine: {
                        show: true,
                    },
                    axisTick: {
                        show: true,
                    },
                    splitLine: {
                        show: true,
                        lineStyle: {
                            type: "dashed",
                        },
                    },
                },
                series: [
                    {
                        type: "line",
                        name: "보정후",
                        data: this.afterCorrection ?? [],
                        connectNulls: false,
                        smooth: true,
                        lineStyle: {
                            color: "#E53935",
                            width: 1,
                        },
                        itemStyle: {
                            color: "#E53935",
                        },
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: "rgba(236, 239, 241, 0.8)" }, // #ECEFF1, 투명도 0.3
                                { offset: 1, color: "rgba(207, 216, 220, 0.5)" }, // #CFD8DC, 투명도 0.1
                            ]),
                        },
                        markLine: {
                            symbol: "none",
                            lineStyle: {
                                color: "#BDBDBD",
                                type: "solid",
                                width: 1,
                                opacity: 0.5,
                            },
                            label: {
                                show: false,
                            },
                            data: verticalLines,
                        },
                    },
                    {
                        type: "line",
                        name: "보정전",
                        data: this.beforeCorrection ?? [],
                        connectNulls: false,
                        smooth: true,
                        z: 1,
                        lineStyle: {
                            color: "#1E88E5",
                        },
                        itemStyle: {
                            color: "#1E88E5",
                        },
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: "rgba(236, 239, 241, 0.3)" }, // #ECEFF1, 투명도 0.3
                                { offset: 1, color: "rgba(207, 216, 220, 0.1)" }, // #CFD8DC, 투명도 0.1
                            ]),
                        },
                    },
                ],
            };
            // if (!this.isEmpty(this.missingSection)) {
            //     option.series.push({
            //         type: "line",
            //         name: "결측",
            //         data: [],
            //         silent: true,
            //         showSymbol: false,
            //         itemStyle: {
            //             color: "#FFCDD2",
            //         },
            //         markArea: {
            //             silent: true,
            //             data: [...(this.missingSection ?? [])],
            //             z: -1,
            //             itemStyle: {
            //                 color: "#FFCDD2",
            //                 opacity: 0.8,
            //             },
            //         },
            //     });
            // }
            if (this.useType === "correction") {
                // 이상치 마커를 모든 시리즈에 추가
                const abnormalMarkPoint = {
                    data: [...(this.abnormalMarker ?? [])],
                    symbol: "pin",
                    symbolSize: 40,
                    symbolOffset: [0, -10],
                    itemStyle: {
                        color: "#f00",
                        shadowBlur: 10,
                        shadowColor: "#333",
                    },
                    tooltip: {
                        trigger: "item",
                    },
                    label: {
                        formatter: (params) => {
                            if (params.name === "Abnormal") {
                                return `{boldStyle|이상}`;
                            }
                            return params.name;
                        },
                        rich: {
                            boldStyle: {
                                fontWeight: 700,
                                color: "#E0E0E0",
                                fontSize: 10,
                            },
                        },
                    },
                };
                option.series.forEach((series) => {
                    series.markPoint = abnormalMarkPoint;
                });

                // 이상치 area를 별도 시리즈로 추가
                if (!this.isEmpty(this.abnormalDefine)) {
                    option.series.push({
                        type: "line",
                        name: "이상치구간",
                        data: [],
                        silent: true,
                        showSymbol: false,
                        itemStyle: {
                            color: "#BDBDBD",
                        },
                        markArea: {
                            silent: true,
                            data: [...(this.abnormalDefine ?? [])],
                            z: -1,
                            itemStyle: {
                                color: "#BDBDBD",
                                opacity: 0.3,
                            },
                        },
                    });
                }

                // 결측 area를 별도 시리즈로 추가
                if (!this.isEmpty(this.missingSection)) {
                    option.series.push({
                        type: "line",
                        name: "결측",
                        data: [],
                        silent: true,
                        showSymbol: false,
                        itemStyle: {
                            color: "#FFCDD2",
                        },
                        markArea: {
                            silent: true,
                            data: [...(this.missingSection ?? [])],
                            z: -1,
                            itemStyle: {
                                color: "#FFCDD2",
                                opacity: 0.8,
                            },
                        },
                    });
                }

                if (!this.isEmpty(this.eventDetail)) {
                    let regDt;
                    let notiText;
                    if (this.eventDetail.alarmType == "AbnormalData") {
                        regDt = this.eventDetail.regDt
                            ? new Date(moment(this.eventDetail.regDt.toString(), "YYYYMMDDHHmmss"))
                            : "";
                        notiText = `이상치 발생 시점`;
                    } else if (this.eventDetail.alarmType == "MissingData") {
                        regDt = this.eventDetail.startDt
                            ? new Date(moment(this.eventDetail.startDt.toString(), "YYYYMMDDHHmmss"))
                            : "";
                        notiText = `결측 발생 시점`;
                    } else regDt = "";

                    // 모든 데이터 시리즈에 마크라인추가
                    const errorMarkLine = {
                        symbol: "none",
                        lineStyle: {
                            color: "red",
                            type: "dashed",
                            width: 2,
                        },
                        label: {
                            show: true,
                            position: "end",
                            formatter: notiText,
                            color: "red",
                            fontSize: 12,
                            fontWeight: "bold",
                        },
                        data: [{ xAxis: regDt }],
                    };

                    option.series.forEach((series) => {
                        series.markLine = errorMarkLine;
                    });
                }
            }
            this.chart.setOption(option);

            this.chart.on("click", (params) => {
                if (params.componentType === "markPoint" && params.name === "Abnormal") {
                    this.$emit("showModal", params.data.abnormalData.regDt);
                }
            });

            // legend 클릭 이벤트 처리
            this.chart.on("legendselectchanged", () => {
                this.updateYAxisScale();
            });
        },

        updateYAxisScale() {
            if (!this.chart) return;

            // 현재 차트 옵션에서 legend 선택 상태 가져오기
            const option = this.chart.getOption();
            const legendSelected = option.legend[0].selected;

            let maxVal = 0;

            // 보정후 데이터가 표시되는 경우 - 해당 데이터의 최대값 계산
            if (legendSelected["보정후"]) {
                const afterVals = this.afterCorrection.filter((v) => v !== null).map((v) => v[1]);
                const maxAfter = afterVals.length > 0 ? Math.max(...afterVals) : 0;
                maxVal = Math.max(maxVal, maxAfter);
            }

            // 보정전 데이터가 표시되는 경우 - 해당 데이터의 최대값 계산
            if (legendSelected["보정전"]) {
                const beforeVals = this.beforeCorrection.filter((v) => v !== null).map((v) => v[1]);
                const maxBefore = beforeVals.length > 0 ? Math.max(...beforeVals) : 0;
                maxVal = Math.max(maxVal, maxBefore);
            }

            // 데이터가 모두 0인 경우 기본값 설정
            if (maxVal === 0) {
                maxVal = 1;
            }

            // 소수점 데이터 처리 개선
            let margin;
            if (maxVal < 0.1) {
                // 매우 작은 값들 (0.1 미만)
                margin = Math.max(maxVal * 0.5, 0.05);
            } else if (maxVal < 1) {
                // 0.1 ~ 1 사이의 값들
                margin = Math.max(maxVal * 0.3, 0.1);
            } else {
                // 1 이상의 값들
                margin = maxVal * 0.25;
            }

            let adjustedMax = maxVal + margin;

            // Y축 최대값 계산 로직 개선
            let newYAxisMax;
            if (adjustedMax < 0.1) {
                // 0.1 미만: 0.05 단위로 반올림
                newYAxisMax = Math.ceil(adjustedMax * 20) / 20;
            } else if (adjustedMax < 1) {
                // 0.1 ~ 1 사이: 0.1 단위로 반올림
                newYAxisMax = Math.ceil(adjustedMax * 10) / 10;
            } else if (adjustedMax < 10) {
                // 1 ~ 10 사이: 0.5 단위로 반올림
                newYAxisMax = Math.ceil(adjustedMax * 2) / 2;
            } else {
                // 10 이상: 정수로 반올림
                newYAxisMax = Math.ceil(adjustedMax);
            }

            // 최소값 보장
            if (newYAxisMax < 0.1) {
                newYAxisMax = 0.1;
            }

            // 차트의 Y축을 새로운 최대값으로 업데이트
            this.chart.setOption({
                yAxis: {
                    min: 0,
                    max: newYAxisMax,
                    // axisLabel: {
                    //     show: true,
                    //     formatter: (value) => {
                    //         if (value === 0) return "0";
                    //         if (newYAxisMax <= 1) {
                    //             return value.toFixed(1);
                    //         }
                    //         return Math.round(value);
                    //     },
                    // },
                    axisLabel: {
                        show: true,
                        formatter: (value) => {
                            if (value === 0) return "0";
                            return Number.isInteger(value) ? value : value.toFixed(1);
                        },
                    },
                    axisLine: {
                        show: true,
                    },
                    axisTick: {
                        show: true,
                    },
                    splitLine: {
                        show: true,
                        lineStyle: {
                            type: "dashed",
                            opacity: 0.3,
                        },
                    },
                },
            });
        },
    },
};
</script>

<style scoped></style>
